import { useEffect, useState, useCallback } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Database, Play, RefreshCw, MapPin, AlertTriangle, CheckCircle2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import JurisdictionPicker from "@/components/JurisdictionPicker";

function StatTile({ label, value, tone = "slate", testId }) {
  const tones = {
    slate: "bg-slate-50 text-slate-900",
    emerald: "bg-emerald-50 text-emerald-900",
    amber: "bg-amber-50 text-amber-900",
    rose: "bg-rose-50 text-rose-900",
  };
  return (
    <div className={`rounded-lg px-4 py-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-outfit font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

export default function AhjBackfillPanel() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [unmatched, setUnmatched] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ahj/backfill/audit");
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnmatched = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ahj/backfill/unmatched");
      setUnmatched(data.unmatched || []);
    } catch (_e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadAudit(); loadUnmatched(); }, [loadAudit, loadUnmatched]);

  const runBackfill = async (dryRun) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/ahj/backfill/run${dryRun ? "?dry_run=true" : ""}`);
      setLastRun({ ...data, dryRun });
      toast.success(
        `${dryRun ? "Dry run" : "Back-fill"} — ${data.stats.newly_matched_place} place · ${data.stats.upgraded_county_to_place} upgrades · ${data.stats.newly_matched_county} county · ${data.stats.unmatched} unmatched`
      );
      await loadAudit();
      await loadUnmatched();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Back-fill failed");
    } finally {
      setRunning(false);
    }
  };

  const handleManualResolve = (row) => {
    setPickerFor({ email: row.email, jurisdiction_label: row.name || row.email });
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="ahj-backfill-panel">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Database className="w-4 h-4 text-indigo-600" />AHJ jurisdiction back-fill
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Re-scores every email contact with the latest fuzzy matcher — recognizes "City of", abbreviations
            ("St." → "Saint", "Ft." → "Fort"), hyphen-state suffixes ("raleigh-nc.gov"), and always prefers a
            specific place over the surrounding county.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={() => runBackfill(true)} disabled={running || loading}
            data-testid="backfill-dryrun-btn"
          >
            Dry run
          </Button>
          <Button
            size="sm" onClick={() => runBackfill(false)} disabled={running || loading}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="backfill-run-btn"
          >
            {running ? <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-2" />}
            Run back-fill
          </Button>
        </div>
      </div>

      {audit ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatTile label="Total contacts" value={audit.total} testId="audit-total" />
            <StatTile label="With place geoid" value={audit.with_geoid} tone="emerald" testId="audit-with-geoid" />
            <StatTile label="County-only" value={audit.with_fips_only} tone="amber" testId="audit-county-only" />
            <StatTile label="Missing both" value={audit.missing_both} tone="rose" testId="audit-missing-both" />
          </div>

          {audit.by_state.length > 0 && (
            <div className="rounded-lg border border-slate-200 overflow-hidden mb-4" data-testid="audit-state-breakdown">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">State</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Geoid</th>
                    <th className="px-3 py-2 text-right">County-only</th>
                    <th className="px-3 py-2 text-right">Missing both</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.by_state.map((s) => (
                    <tr key={s.state} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{s.state}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.total}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{s.with_geoid}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">{s.with_fips_only}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700">{s.missing_both}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-slate-500 py-4">{loading ? "Loading audit…" : "No audit available."}</div>
      )}

      {lastRun && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-4" data-testid="backfill-last-run">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <strong className="text-sm text-emerald-900">{lastRun.dryRun ? "Dry run complete" : "Back-fill complete"}</strong>
            <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">{lastRun.stats.processed} contacts</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <div><span className="text-emerald-700">New place</span><div className="font-mono tabular-nums">{lastRun.stats.newly_matched_place}</div></div>
            <div><span className="text-emerald-700">Upgrades</span><div className="font-mono tabular-nums">{lastRun.stats.upgraded_county_to_place}</div></div>
            <div><span className="text-emerald-700">New county</span><div className="font-mono tabular-nums">{lastRun.stats.newly_matched_county}</div></div>
            <div><span className="text-emerald-700">Kept</span><div className="font-mono tabular-nums">{lastRun.stats.kept_place + lastRun.stats.kept_county}</div></div>
            <div><span className="text-emerald-700">Unmatched</span><div className="font-mono tabular-nums">{lastRun.stats.unmatched}</div></div>
          </div>
          {lastRun.sample_changes?.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 mb-1">Sample changes</div>
              <ul className="space-y-1">
                {lastRun.sample_changes.slice(0, 5).map((c, i) => (
                  <li key={i} className="text-xs text-emerald-900 flex items-center gap-1.5 font-mono">
                    <span className="text-emerald-600">{c.email}</span>
                    <span className="text-emerald-500">{c.from || "—"}</span>
                    <ArrowRight className="w-3 h-3" />
                    <span className="font-semibold">{c.to}</span>
                    <Badge variant="outline" className="ml-1 text-[10px]">{c.confidence}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Unmatched panel */}
      <div data-testid="backfill-unmatched-panel">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            Unmatched — manual review ({unmatched?.length ?? "—"})
          </h4>
          <Button variant="ghost" size="sm" onClick={loadUnmatched} className="h-7"><RefreshCw className="w-3 h-3 mr-1" />Reload</Button>
        </div>
        {!unmatched || unmatched.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
            No unmatched contacts. <span className="text-emerald-700">All caught up.</span>
          </div>
        ) : (
          <ul className="space-y-2">
            {unmatched.map((u) => (
              <li key={u.email} className="border border-amber-200 bg-amber-50/50 rounded-md p-3 text-xs" data-testid={`unmatched-${u.email}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-slate-900">{u.email}</div>
                    {u.name ? <div className="text-slate-500 mt-0.5">{u.name}</div> : null}
                    <div className="text-amber-700 font-mono text-[10px] mt-1">{u.reason}</div>
                    {u.sample_subjects?.length ? (
                      <div className="text-slate-500 mt-1 italic">e.g. {u.sample_subjects[0]}</div>
                    ) : null}
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleManualResolve(u)}
                    className="text-xs"
                    data-testid={`manual-link-${u.email}`}
                  >
                    <MapPin className="w-3 h-3 mr-1" />Manually link
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <JurisdictionPicker
        open={!!pickerFor}
        contact={pickerFor}
        onClose={() => setPickerFor(null)}
        onResolved={(picked) => {
          toast.success(`Linked ${pickerFor?.email} → ${picked.label}`);
          loadAudit();
          loadUnmatched();
        }}
      />
    </div>
  );
}
